import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { SequenceDetail as ISequenceDetail, CreatorSequence, Sequence } from '@/types/sequence';
import { getSequenceDetails, getCreatorSequences } from '@/api/sequences';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { useRatingStats } from '@/hooks/useRatingStats';
import { useFavorites } from '@/contexts/FavoritesContext';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { toast } from 'sonner';
import { 
  Box, 
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Rating,
  Breadcrumbs,
  Skeleton,
  Alert,
  Stack,
  Divider,
  Avatar
} from '@mui/material';
import {
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Download,
  AccessTime,
  Star,
  Share,
  ArrowBack,
  Settings
} from '@mui/icons-material';
import SequenceCreatorCard from '@/components/sequences/SequenceCreatorCard';
import AuthModal from '@/components/auth/AuthModal';
import { ClaimButton } from '@/components/sequences/ownership';
import SequenceProps from '@/components/sequences/SequenceProps';
import RatingSummary from '@/components/reviews/RatingSummary';
import RecentReviewsHighlight from '@/components/reviews/RecentReviewsHighlight';
import { 
  SchemaMarkup, 
  createProductSchema, 
  createCreativeWorkSchema, 
  createBreadcrumbSchema 
} from '@/components/seo/SchemaMarkup';
import ReviewSection from '@/components/reviews/ReviewSection';
import { useSequencePurchase } from '@/hooks/useSequencePurchase';
import { useDownloadFlowStore } from '@/stores/useDownloadFlowStore';
import { AddedToDisplayModal } from '@/components/download/AddedToDisplayModal';
import { GoFundMeDonationModal } from '@/components/download/GoFundMeDonationModal';
import { ConfirmationPage } from '@/components/download/ConfirmationPage';

const SequenceDetailPage: React.FC = () => {
  console.log('SequenceDetail component rendering');
  const { id } = useParams<{ id: string }>();
  const [sequence, setSequence] = useState<ISequenceDetail | null>(null);
  const [creatorSequences, setCreatorSequences] = useState<CreatorSequence[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalAction, setAuthModalAction] = useState<'favorite' | 'download'>('download');
  const { user, isAuthenticated } = useAuth();
  const { stats: ratingStats, isLoading: isLoadingRating } = useRatingStats('sequence', id || '');
  const navigate = useNavigate();
  const { isSequenceFavorite, toggleSequenceFavorite } = useFavorites();

  // Use the sequence purchase hook for download functionality
  const {
    isOwner,
    isPurchased,
    canDownload,
    handleDownload: handleSequenceDownload,
    isDownloading,
    checkingOwnership,
  } = useSequencePurchase(id);

  // Download flow store for modal management
  const {
    modalState,
    currentSession,
    hideAddedToDisplayModal,
    hideDonationModal,
    hideConfirmationPage,
    endDownloadSession,
  } = useDownloadFlowStore();

  // Admin check
  const isAdmin = user?.email === 'joey@towb.in';

  useEffect(() => {
    console.log('Loading sequence with ID:', id);
    const loadData = async () => {
      try {
        if (!id) throw new Error('No sequence ID provided');
        
        // Load sequence details
        const sequenceData = await getSequenceDetails(id);
        setSequence(sequenceData);
        
        // Load creator's other sequences if we have a valid creator ID
        if (sequenceData.creator && sequenceData.creator.id && 
            sequenceData.creator.id !== 'unknown' && 
            sequenceData.creator.id !== 'null' && 
            sequenceData.creator.id.trim() !== '') {
          const creatorData = await getCreatorSequences(sequenceData.creator.id);
          // Filter out the current sequence and limit to 4 items
          const otherSequences = creatorData.sequences
            .filter(seq => seq.id !== id)
            .slice(0, 4);
          setCreatorSequences(otherSequences);
        } else {
          // Set empty array if no valid creator ID
          setCreatorSequences([]);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error loading data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load sequence');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  console.log('Current state:', { loading, error, sequence });

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <NavBar />
        <Container maxWidth="lg" sx={{ py: 4, mt: 12 }}>
          <Skeleton variant="rectangular" height={400} />
          <Box sx={{ mt: 2 }}>
            <Skeleton variant="text" height={60} />
            <Skeleton variant="text" height={30} />
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <Box sx={{ flex: '0 0 66.666667%' }}>
                <Skeleton variant="rectangular" height={200} />
              </Box>
              <Box sx={{ flex: '0 0 33.333333%' }}>
                <Skeleton variant="rectangular" height={200} />
              </Box>
            </Box>
          </Box>
        </Container>
        <Footer />
      </Box>
    );
  }

  if (error || !sequence) {
    return (
      <Box sx={{ 
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <NavBar />
        <Container maxWidth="lg" sx={{ py: 4, mt: 12 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || 'Sequence not found'}
          </Alert>
          <Button component={Link} to="/sequences" variant="contained">
            Back to Sequences
          </Button>
        </Container>
        <Footer />
      </Box>
    );
  }

  console.log('Rendering sequence details:', sequence);

  const handleFavorite = () => {
    if (!isAuthenticated) {
      setAuthModalAction('favorite');
      setShowAuthModal(true);
      return;
    }
    
    if (id) {
      toggleSequenceFavorite(id);
      if (isSequenceFavorite(id)) {
        toast.success("Removed from favorites", {
          description: "Sequence removed from your favorites"
        });
      } else {
        toast.success("Added to favorites", {
          description: "Sequence added to your favorites"
        });
      }
    }
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    
    if (authModalAction === 'favorite' && id) {
      toggleSequenceFavorite(id);
      toast.success("Added to favorites", {
        description: "Sequence added to your favorites"
      });
    } else if (authModalAction === 'download' && sequence) {
      // Trigger download after successful authentication
      setTimeout(() => {
        const sequenceForDownload: Sequence = {
          id: sequence.id,
          title: sequence.title,
          displayName: sequence.title,
          price: sequence.price,
          isFree: sequence.isFree,
          software: sequence.software,
          holiday: sequence.holiday,
          imageUrl: sequence.imageUrl,
          song: sequence.song,
          createdAt: sequence.createdAt,
          downloadCount: sequence.downloadCount,
          averageRating: sequence.averageRating,
          ratingCount: sequence.ratingCount,
          featured: sequence.featured,
          file_url: sequence.file_url,
          creator: sequence.creator,
        };
        handleSequenceDownload(sequenceForDownload);
      }, 100);
    }
  };

  const handleDownload = () => {
    console.log('Download button clicked:', { 
      isAuthenticated, 
      price: sequence?.price, 
      isFree: sequence?.isFree,
      isOwner, 
      isPurchased, 
      canDownload 
    });
    
    if (!isAuthenticated) {
      setAuthModalAction('download');
      setShowAuthModal(true);
      return;
    }
    
    if (sequence) {
      // Convert SequenceDetail to Sequence format expected by the hook
      const sequenceForDownload: Sequence = {
        id: sequence.id,
        title: sequence.title,
        displayName: sequence.title,
        price: sequence.price,
        isFree: sequence.isFree,
        software: sequence.software,
        holiday: sequence.holiday,
        imageUrl: sequence.imageUrl,
        song: sequence.song,
        createdAt: sequence.createdAt,
        downloadCount: sequence.downloadCount,
        averageRating: sequence.averageRating,
        ratingCount: sequence.ratingCount,
        featured: sequence.featured,
        file_url: sequence.file_url,
        creator: sequence.creator,
      };
      
      console.log('Calling handleSequenceDownload with:', sequenceForDownload);
      // The handleSequenceDownload already handles modal flow and progress
      handleSequenceDownload(sequenceForDownload);
    }
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: sequence.title,
          text: `Check out this ${sequence.holiday} light sequence: ${sequence.title}`,
          url: window.location.href
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  // Handle admin edit button click
  const handleAdminEdit = () => {
    if (!id) return;
    // Open admin panel in new tab with sequence pre-selected
    const adminUrl = `/admin?sequence=${encodeURIComponent(id)}`;
    window.open(adminUrl, '_blank');
  };

  // Generate schema markup for sequence
  const generateSchemaMarkup = () => {
    if (!sequence) return null;

    const currentUrl = window.location.href;

    // Product Schema for commercial aspects
    const productSchema = createProductSchema(
      sequence.title,
      sequence.description,
      sequence.imageUrl || sequence.videoUrl,
      {
        price: sequence.price.toString(),
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        url: currentUrl
      },
      'Light Display Sequence',
      `${sequence.holiday} ${sequence.software}`,
      {
        ratingValue: sequence.averageRating,
        reviewCount: sequence.ratingCount
      }
    );

    // Creative Work Schema for artistic aspects
    const creativeWorkSchema = createCreativeWorkSchema(
      sequence.title,
      sequence.description,
      {
        name: sequence.creator?.username || 'Unknown Creator',
        url: sequence.creator?.id && sequence.creator.id !== 'unknown' ? 
          `${window.location.origin}/creator/${sequence.creator.id}` : 
          window.location.origin
      },
      sequence.createdAt,
      sequence.song?.genre || sequence.holiday,
      [
        sequence.holiday,
        sequence.software,
        'light sequence',
        'holiday lights',
        'synchronized lights',
        sequence.song?.title || sequence.title,
        sequence.song?.artist || 'Unknown Artist'
      ],
      currentUrl,
      sequence.imageUrl,
      'Commercial use allowed'
    );

    // Breadcrumb Schema
    const breadcrumbSchema = createBreadcrumbSchema([
      { name: 'Home', url: window.location.origin },
      { name: 'Sequences', url: `${window.location.origin}/sequences` },
      { name: sequence.software, url: `${window.location.origin}/sequences/${sequence.software.toLowerCase()}` },
      { name: sequence.holiday, url: `${window.location.origin}/sequences/${sequence.software.toLowerCase()}/${sequence.holiday.toLowerCase()}` },
      { name: sequence.title, url: currentUrl }
    ]);

    return { productSchema, creativeWorkSchema, breadcrumbSchema };
  };

  const schemas = generateSchemaMarkup();

  // Generate SEO content
  const pageTitle = `${sequence?.title} - ${sequence?.software} ${sequence?.holiday} Light Sequence | LightsHunt`;
  const metaDescription = `Download ${sequence?.title} by ${sequence?.song?.artist || 'Unknown Artist'}, a ${sequence?.holiday} light sequence for ${sequence?.software}. ${sequence?.description} Price: $${sequence?.price}`;
  const keywords = `${sequence?.title}, ${sequence?.song?.artist || 'Unknown Artist'}, ${sequence?.holiday} lights, ${sequence?.software}, light sequence, holiday display, synchronized lights, ${sequence?.song?.title || sequence?.title}`;

  return (
    <>
      {sequence && (
        <>
          <Helmet>
            <title>{pageTitle}</title>
            <meta name="description" content={metaDescription} />
            <meta name="keywords" content={keywords} />
            
            {/* Open Graph tags */}
            <meta property="og:title" content={pageTitle} />
            <meta property="og:description" content={metaDescription} />
            <meta property="og:type" content="product" />
            <meta property="og:url" content={window.location.href} />
            {sequence.imageUrl && <meta property="og:image" content={sequence.imageUrl} />}
            <meta property="product:price:amount" content={sequence.price.toString()} />
            <meta property="product:price:currency" content="USD" />
            
            {/* Twitter Card tags */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={pageTitle} />
            <meta name="twitter:description" content={metaDescription} />
            {sequence.imageUrl && <meta name="twitter:image" content={sequence.imageUrl} />}
            
            {/* Music/Audio specific meta tags */}
            <meta property="music:song" content={sequence.song?.title || sequence.title} />
            <meta property="music:musician" content={sequence.song?.artist || 'Unknown Artist'} />
            <meta property="music:album" content={sequence.song?.album || ''} />
            
            {/* Canonical URL */}
            <link rel="canonical" href={window.location.href} />
          </Helmet>

          {/* Schema Markup */}
          {schemas && (
            <>
              <SchemaMarkup schema={schemas.productSchema} />
              <SchemaMarkup schema={schemas.creativeWorkSchema} />
              <SchemaMarkup schema={schemas.breadcrumbSchema} />
            </>
          )}
        </>
      )}

      <Box sx={{ 
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <NavBar />
        <Container maxWidth="lg" sx={{ py: 4, mt: 12 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs sx={{ mb: 2 }}>
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
              Home
            </Link>
            <Link to="/sequences" style={{ textDecoration: 'none', color: 'inherit' }}>
              Sequences
            </Link>
            <Link 
              to={`/sequences/${sequence.software.toLowerCase()}`} 
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              {sequence.software}
            </Link>
            <Link 
              to={`/sequences/${sequence.software.toLowerCase()}/${sequence.holiday.toLowerCase()}`} 
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              {sequence.holiday}
            </Link>
            <Typography color="text.primary">{sequence.title}</Typography>
          </Breadcrumbs>

          {/* Title Section */}
          <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
            {sequence.title}
          </Typography>

          {/* Main Content */}
          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 2, md: 4 }, 
            flexDirection: { xs: 'column', md: 'row' }
          }}>
            {/* Left Column */}
            <Box sx={{ 
              flex: { xs: '1 1 100%', md: '1 1 66.666667%' },
              minWidth: 0 
            }}>
              {/* Video Player / Featured Image */}
              <Box sx={{ 
                position: 'relative',
                paddingBottom: '56.25%', // 16:9 aspect ratio
                height: 0,
                overflow: 'hidden',
                borderRadius: 1,
                mb: 3
              }}>
                {sequence.videoUrl ? (
                  <iframe
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      border: 0
                    }}
                    src={sequence.videoUrl}
                    title={sequence.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : sequence.imageUrl ? (
                  <Box
                    component="img"
                    src={sequence.imageUrl}
                    alt={`${sequence.title} featured image`}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: 1
                    }}
                    onError={(e) => {
                      console.log('Failed to load sequence image:', sequence.imageUrl);
                      e.currentTarget.src = '/sequence-cover-placeholder.svg';
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'grey.100',
                      borderRadius: 1
                    }}
                  >
                    <Typography variant="body1" color="text.secondary">
                      No media available
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Sequence Info */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {ratingStats && ratingStats.rating_count > 0 ? (
                      <>
                        <Rating value={ratingStats.average_rating} readOnly precision={0.1} />
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          ({ratingStats.average_rating.toFixed(1)}) • {ratingStats.rating_count} {ratingStats.rating_count === 1 ? 'review' : 'reviews'}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Rating value={0} readOnly />
                        <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                          No reviews yet
                        </Typography>
                      </>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      <AccessTime sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                      {formatDistanceToNow(new Date(sequence.createdAt), { addSuffix: true })}
                    </Typography>
                  </Box>

                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {sequence.description}
                  </Typography>

                  {/* More by Creator - Horizontal Carousel */}
                  {creatorSequences.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        More by {sequence.creator?.username || 'Unknown Creator'}
                      </Typography>
                      <Box sx={{ 
                        display: 'flex',
                        gap: 2,
                        overflowX: 'auto',
                        pb: 1,
                        '&::-webkit-scrollbar': {
                          height: 8,
                        },
                        '&::-webkit-scrollbar-track': {
                          backgroundColor: 'rgba(0,0,0,0.1)',
                          borderRadius: 4,
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: 'rgba(0,0,0,0.3)',
                          borderRadius: 4,
                          '&:hover': {
                            backgroundColor: 'rgba(0,0,0,0.5)',
                          },
                        },
                      }}>
                        {creatorSequences.map((seq) => (
                          <Link 
                            key={seq.id} 
                            to={`/sequence/${seq.id}`}
                            style={{ textDecoration: 'none', flexShrink: 0 }}
                          >
                            <Card sx={{ 
                              width: 200,
                              transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: 3,
                              }
                            }}>
                              <CardMedia
                                component="img"
                                height="120"
                                image={seq.imageUrl}
                                alt={seq.title}
                                sx={{ objectFit: 'cover' }}
                              />
                              <CardContent sx={{ p: 2 }}>
                                <Typography 
                                  variant="subtitle2" 
                                  sx={{ 
                                    fontWeight: 600,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    mb: 0.5
                                  }}
                                >
                                  {seq.title}
                                </Typography>
                                <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                                  ${seq.price.toFixed(2)}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Link>
                        ))}
                      </Box>
                    </Box>
                  )}

                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Chip label={sequence.software} />
                    <Chip label={sequence.holiday} />
                    <Chip label={`${sequence.downloadCount} downloads`} />
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="h6" gutterBottom>
                    Song Details
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Title: {sequence.song?.title || sequence.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Artist: {sequence.song?.artist || 'Unknown Artist'}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  {/* Add Props Section */}
                  <SequenceProps 
                    props={sequence.props || []} 
                    currentHoliday={sequence.holiday}
                  />
                </CardContent>
              </Card>
            </Box>

            {/* Right Column */}
            <Box sx={{ 
              flex: { xs: '1 1 100%', md: '0 0 33.333333%' },
              minWidth: 0 
            }}>
              {/* Price and Actions */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    ${sequence.price.toFixed(2)}
                  </Typography>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<Download />}
                    onClick={handleDownload}
                    disabled={isDownloading || (checkingOwnership && sequence.price > 0)}
                    sx={{ mb: 2 }}
                  >
                    {checkingOwnership 
                      ? 'Checking access...' 
                      : isDownloading 
                        ? 'Downloading...' 
                        : sequence.price === 0
                          ? 'Download Free'
                          : canDownload && isPurchased 
                            ? 'Re-download' 
                            : 'Download'
                    }
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={isSequenceFavorite(sequence.id) ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                    onClick={handleFavorite}
                    sx={{ mb: 2 }}
                  >
                    {isSequenceFavorite(sequence.id) ? 'Remove from Favorites' : 'Add to Favorites'}
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<Share />}
                    onClick={handleShare}
                  >
                    Share
                  </Button>
                  
                  {/* Admin Edit Button - only visible to super admin */}
                  {isAdmin && (
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<Settings />}
                      onClick={handleAdminEdit}
                      sx={{ 
                        mt: 2,
                        color: 'orange.main',
                        borderColor: 'orange.main',
                        '&:hover': {
                          backgroundColor: 'orange.50',
                          borderColor: 'orange.main'
                        }
                      }}
                    >
                      Edit in Admin
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Creator Info */}
              <SequenceCreatorCard
                avatarUrl={sequence.creator?.avatarUrl || ''}
                creatorName={sequence.creator?.username || 'Unknown Creator'}
                creatorId={sequence.creator?.id || ''}
                rating={4.5}
                sequenceCount={0}
              />

              {/* Ownership Claim Section */}
              <ClaimButton
                sequenceId={sequence.id}
                creatorId={sequence.creator?.id || ''}
                claimStatus={sequence.ownership?.claimStatus || 'unclaimed'}
              />

              {/* Recent Reviews Sidebar */}
              <Box sx={{ mt: 3, display: { xs: 'none', md: 'block' } }}>
                <RecentReviewsHighlight
                  targetType="sequence"
                  targetId={sequence.id}
                  maxReviews={2}
                  showTitle={true}
                />
              </Box>
            </Box>
          </Box>

          {/* Rating Summary and Recent Reviews */}
          {ratingStats && ratingStats.rating_count > 0 && (
            <Box sx={{ mt: 4, mb: 4 }}>
              <Typography variant="h5" gutterBottom>
                Reviews & Ratings
              </Typography>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                gap: 3
              }}>
                <Box>
                  <RatingSummary 
                    stats={ratingStats}
                    showDistribution={true}
                    showPercentages={true}
                  />
                </Box>
                <Box>
                  <RecentReviewsHighlight
                    targetType="sequence"
                    targetId={sequence.id}
                    maxReviews={3}
                    showTitle={true}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {/* Reviews Section */}
          <Box sx={{ mt: 4 }}>
            <ReviewSection
              targetType="sequence"
              targetId={sequence.id}
              targetName={sequence.title}
            />
          </Box>
        </Container>
        <Footer />
        
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onSuccess={handleAuthSuccess}
          title="Sign in to save favorites"
          description="Sign in to save this sequence to your favorites and access it later."
        />

        {/* Download Flow Modals */}
        {/* Donation Modal - for charity sequences */}
        {modalState.isDonationModalVisible && sequence?.charity && (
          <GoFundMeDonationModal
            isOpen={modalState.isDonationModalVisible}
            onClose={hideDonationModal}
            onProceedToDownload={showAddedToDisplayModal}
            sequenceName={sequence.title}
            charityName={sequence.charity.name || 'Selected Charity'}
          />
        )}

        {/* Added to Display Modal - after download completion */}
        {modalState.isAddedToDisplayModalVisible && currentSession && (
          <AddedToDisplayModal
            isOpen={modalState.isAddedToDisplayModalVisible}
            onClose={() => {
              hideAddedToDisplayModal();
              // Automatically trigger confirmation page after this modal
              setTimeout(() => {
                // The confirmation page will be shown by the download flow store logic
              }, 500);
            }}
            sequenceId={currentSession.sequenceId}
            sequenceTitle={currentSession.sequenceTitle}
            sequenceArtist={currentSession.sequenceArtist}
            onAfterAction={() => {
              console.log('AddedToDisplayModal closed, flow continuing...');
            }}
          />
        )}

        {/* Confirmation Page - final step showing success and related sequences */}
        {modalState.isConfirmationPageVisible && currentSession && creatorSequences.length > 0 && (
          <ConfirmationPage
            isOpen={modalState.isConfirmationPageVisible}
            onContinue={() => {
              hideConfirmationPage();
              endDownloadSession();
              console.log('Download flow completed');
            }}
            sequenceTitle={currentSession.sequenceTitle}
            sequenceArtist={currentSession.sequenceArtist}
            relatedSequences={creatorSequences.slice(0, 3)} // Show up to 3 related sequences
          />
        )}

      </Box>
    </>
  );
};

export default SequenceDetailPage;
